import type { AiChatBotDebugLevel } from './types'

/**
 * Debug levels for the AiChatBot engine.
 *
 * Default during development: `'verbose'` (set in `AI_CHATBOT_CONFIG.debug.defaultLevel`).
 * Change to `'off'` before production release.
 *
 * ---
 * `'off'`
 *   - Nothing logged. All methods are NOOPs, including error and warn.
 *   - Use in production.
 *
 * `'console'`
 *   - Turn start / end grouped by label
 *   - Model name + tool names used per turn
 *   - Tool execution results (name only, no payloads)
 *   - Turn costs + session running total (token counts, model, duration)
 *   - Warnings (e.g. processUserMessage called while already processing)
 *   - Errors (turn failures, tool errors)
 *
 * `'verbose'`
 *   - Everything in `'console'`, plus:
 *   - Full request message array (system prompt + history)
 *   - Full tool definitions sent to the API
 *   - Full completion object from the API (choices, usage, etc.)
 *   - Stage timings (gather duration in ms)
 *   - Raw stream snapshots during mid-stream tool dispatch
 *
 * ---
 * Usage: all engine code must call `this.log.*` or a passed-in `Logger`.
 * Never use bare `console.*` in engine/ files.
 */

const PREFIX = '[AiChatBot]'

export interface Logger {
  level: AiChatBotDebugLevel
  log: (...args: unknown[]) => void
  warn: (...args: unknown[]) => void
  error: (...args: unknown[]) => void
  group: (label: string) => void
  groupEnd: () => void
  /** Verbose-only: full payloads, stage timings, raw stream frames. */
  verbose: (...args: unknown[]) => void
}

const NOOP = () => {}

export function createLogger(level: AiChatBotDebugLevel): Logger {
  if (level === 'off') {
    return {
      level,
      log: NOOP,
      warn: NOOP,
      error: NOOP,
      group: NOOP,
      groupEnd: NOOP,
      verbose: NOOP,
    }
  }

  const verbose =
    level === 'verbose'
      ? (...args: unknown[]) => console.debug(PREFIX, ...args)
      : NOOP

  return {
    level,
    log: (...args) => console.log(PREFIX, ...args),
    warn: (...args) => console.warn(PREFIX, ...args),
    error: (...args) => console.error(PREFIX, ...args),
    group: (label) => console.group(`${PREFIX} ${label}`),
    groupEnd: () => console.groupEnd(),
    verbose,
  }
}
